// LÉPÉS-JELZŐ (stepper) — a jegy-nyitó vezetett űrlapjának feje (a terv `.stepper` mintája).
//
// A `docs/foundations/design.md` §9 szerint PARTNER-ONLY, amíg nincs máshol vezetett űrlap: a belső
// felület űrlapjai egy-lapos, sűrű mezőlisták (ott a stepper lassítana). A promóció szabálya a 2. használat.
//
// MOBILON a lépés-FELIRAT elmarad (csak a szám/pipa látszik) — három felirat 375px-en tördelne, és a lépés
// jelentését a lap tartalma amúgy is elmondja.
//
// NAVIGÁLHATÓ (user-kérés): `onStepSelect` mellett a számok GOMBOK, és a `stepEnabled` dönti el,
// melyik érhető el. MIÉRT A HÍVÓ DÖNT: az elérhetőség ŰRLAP-szabály (a köztes lépések érvényessége), nem
// megjelenítés — a stepper nem tudja, mi számít kitöltöttnek. `onStepSelect` nélkül a jelző marad, ami volt:
// nem-interaktív `span`-ok (így a komponens minden korábbi használata bitre azonos).
import { CheckIcon } from '@heroicons/react/24/outline';

import { cn } from '@/lib/utils';

export function PortalStepper({
  steps,
  current,
  ariaLabel,
  onStepSelect,
  stepEnabled,
  className,
}: {
  steps: readonly string[];
  current: number;
  ariaLabel: string;
  /** Extra osztályok a jelzőn (pl. `shrink-0` egy ragadós fej-sávban). */
  className?: string | undefined;
  /** Megadva a lépések KATTINTHATÓAK (gomb); elhagyva a jelző csak megjelenít. */
  onStepSelect?: (step: number) => void;
  /** Elérhető-e az adott lépés (a hívó űrlap-szabálya). Alap: minden elérhető. */
  stepEnabled?: (step: number) => boolean;
}): JSX.Element {
  return (
    <ol aria-label={ariaLabel} className={cn('mb-6 flex items-center gap-0', className)}>
      {steps.map((label, i) => {
        const done = i < current;
        const active = i === current;
        const clickable = onStepSelect !== undefined && (stepEnabled?.(i) ?? true) && !active;
        const bubble = cn(
          'ease-atlas inline-flex size-[26px] items-center justify-center rounded-full border-[1.5px] text-xs font-semibold transition-all duration-200 [&>svg]:size-3',
          active && 'bg-brand border-brand text-white',
          done && 'bg-brand-soft border-brand text-brand',
          !active && !done && 'bg-card border-border text-muted-foreground',
          // A kattintható lépés HOVER-visszajelzést kap; a nem elérhető lépés halványabb és `cursor-default`
          // (nem `disabled` attribútum a `span`-on — a gomb-ág kezeli).
          clickable && 'hover:border-brand hover:text-brand cursor-pointer',
        );
        const text = cn(
          'hidden text-[12.5px] font-medium sm:inline',
          active ? 'text-foreground' : 'text-muted-foreground',
          clickable && 'hover:text-foreground',
        );

        return (
          <li key={label} className="flex flex-1 items-center last:flex-none">
            {clickable ? (
              // EGY gomb a számmal ÉS a felirattal: a kattintható felület így nem egy 26px-es kör (mobilon
              // az érintés-cél túl kicsi lenne), és a képernyőolvasó egy értelmes nevet kap.
              <button
                type="button"
                onClick={() => onStepSelect(i)}
                className="focus-visible:ring-ring flex shrink-0 items-center gap-2.5 rounded-full focus:outline-none focus-visible:ring-2"
                aria-label={label}
              >
                <span className={bubble}>{done ? <CheckIcon strokeWidth={2.4} /> : i + 1}</span>
                <span className={text}>{label}</span>
              </button>
            ) : (
              <div className="flex shrink-0 items-center gap-2.5">
                <span
                  aria-current={active ? 'step' : undefined}
                  className={cn(bubble, onStepSelect !== undefined && !active && 'opacity-60')}
                >
                  {done ? <CheckIcon strokeWidth={2.4} /> : i + 1}
                </span>
                <span className={text}>{label}</span>
              </div>
            )}
            {i < steps.length - 1 && (
              <span
                aria-hidden="true"
                className={cn(
                  'mx-3 h-[1.5px] min-w-4 flex-1',
                  i < current ? 'bg-brand' : 'bg-border',
                )}
              />
            )}
          </li>
        );
      })}
    </ol>
  );
}
